import * as THREE from "three";
import { LevelSim, SimConstants, SimEventKind } from "@highmarch/domain";
import type { Decal, VFX } from "../../../../../art/vfx.js";
import { AnimRig } from "../rig";
import { GroundMesh } from "../groundMesh";
import { lib } from "./common";
import { TOWER_SHOT, releaseSeconds } from "./shotTable";

/**
 * The Mortar's shells in flight (TOWERS_V2, docs/TOWERS_V2.md "Mortar"). The sim lobs a shell at a landing point and
 * lands it `shellFlightTicks` later (ShellLaunched … ShellLanded), so the shell is flown on the SIM's clock rather than
 * in real time like the other rounds (views/projectiles.ts): its place on the arc is the share of its flight ticks gone
 * plus the frame's tick fraction. It lands on the frame the sim bursts it, at ×1, ×2 and ×3, and it hangs in the air
 * while a card offer holds the sim.
 *
 * Each shell: a landing marker on the ground from the launch (a ring the burst's size that tightens and brightens as
 * the shell falls: the telegraph), the `proj_shell` model leaving the bore on the `fire` clip's release frame and
 * tumbling along a high lob with a powder-smoke trail, and on landing the heavy burst (vfx.js mortarImpact: fire
 * ball, dirt, dust ring, scorch, a light camera kick) sized to the burst radius. Reduced effects keep the marker, the
 * shell and a plain burst, without the trail, the smoke and the shake.
 */

export interface MortarShellView {
  readonly id: number;
  readonly towerId: number;
  /** Where it lands (world; y is the ground there) and the burst radius (cells). */
  readonly to: THREE.Vector3;
  readonly radius: number;
  /** Sim ticks of flight, and how many have gone (the sim's shell's own count), and ticks it has been missing from the sim. */
  readonly flight: number;
  elapsed: number;
  missing: number;
  /** The share of the flight (0–1) at which the clip releases the shell; the muzzle point read then. */
  readonly release: number;
  from: THREE.Vector3 | null;
  obj: THREE.Object3D | null;
  rig: AnimRig | null;
  /** Distance left before the next trail puff, and its last drawn place. */
  toNext: number;
  /** The landing marker: a ground decal (vfx.js type `target`, laid over the terrain) driven by the flight. */
  marker: Decal | null;
  readonly last: THREE.Vector3;
  spin: number;
}

/** The shell's lob: an apex this high over the higher end, plus this much per cell of range (a mortar lobs HIGH). */
export const SHELL_APEX = { base: 0.9, perCell: 0.24 } as const;
/** The telegraph ring: alpha at launch and at landing, its size from the launch to the landing (× the burst), colour. */
export const SHELL_MARKER = { alpha0: 0.35, alpha1: 1.0, scale0: 1.3, scale1: 1.0, color: [1, 0.5, 0.16] as [number, number, number], bright: 1.4 } as const;
/** The marker's size over the burst's at flight share `p`: it closes in on the burst as the shell falls. Pure. */
export function markerScale(p: number): number { const k = Math.max(0, Math.min(1, p)); return SHELL_MARKER.scale0 + (SHELL_MARKER.scale1 - SHELL_MARKER.scale0) * k * k; }
/** Camera kick of a landing (vfx.js mortarImpact's trauma; the cannon's is 0.25): a light thump, not a quake. */
export const SHELL_TRAUMA = 0.14;
/** World units between two powder puffs of the shell's trail (sparser than the cannonball's 0.12: it flies longer). */
export const SHELL_TRAIL_SPACING = 0.22;
/** The shell model's drawn size over its modelled one (proj_shell, a finned shell: drawn larger it read as a rocket). */
export const SHELL_SCALE = 1.1;

/**
 * Point on the lob `t` (0 = the muzzle, 1 = the landing point) of a shell from `from` to `to`: a parabola over the
 * straight line, peaking `apex` above the chord's middle. Pure.
 */
export function shellArc(from: THREE.Vector3, to: THREE.Vector3, t: number, out = new THREE.Vector3()): THREE.Vector3 {
  const k = Math.max(0, Math.min(1, t));
  out.lerpVectors(from, to, k);
  out.y += 4 * shellApex(from, to) * k * (1 - k);
  return out;
}
/** The lob's height over the chord at mid-flight for a shell from `from` to `to`. */
export function shellApex(from: THREE.Vector3, to: THREE.Vector3): number {
  return SHELL_APEX.base + SHELL_APEX.perCell * Math.hypot(to.x - from.x, to.z - from.z);
}

/**
 * The share (0–1) of a shell's flight its `fire` clip takes to release it: the release frame at the clip's rate, over
 * the flight's sim seconds. Both run on the sim's clock (the tower clips play on sim time), so game speed cancels.
 */
export function shellRelease(flightTicks: number, rate = 1): number {
  const seconds = flightTicks / SimConstants.TicksPerSecond;
  return seconds > 0 ? Math.min(0.6, releaseSeconds(TOWER_SHOT.mortar, rate) / seconds) : 0;
}

/** Where the shell is drawn at flight share `p` (null before its release). */
export function shellAt(s: Pick<MortarShellView, "release" | "from" | "to">, p: number, out = new THREE.Vector3()): THREE.Vector3 | null {
  if (p < s.release || !s.from) return null;
  return shellArc(s.from, s.to, (p - s.release) / Math.max(1e-3, 1 - s.release), out);
}

const _p = new THREE.Vector3(), _v = new THREE.Vector3(), _t = new THREE.Vector3(), _fwd = new THREE.Vector3(0, 0, -1);

export class MortarShells {
  readonly group = new THREE.Group();
  readonly live = new Map<number, MortarShellView>();
  private readonly pool: { obj: THREE.Object3D; rig: AnimRig }[] = [];
  /** Set by the owner: a tower node's world position after the rig update (TowerViews.nodeWorld). */
  towerNode: ((towerId: number, part: string) => THREE.Vector3 | null) | null = null;
  /** Set by the owner: fired as a shell leaves the bore (sound), and as it lands. */
  onRelease: ((s: MortarShellView, flightSeconds: number) => void) | null = null;
  onLand: ((at: THREE.Vector3, radius: number) => void) | null = null;

  constructor(private readonly sim: LevelSim, private readonly fx: () => VFX | null, private readonly reduced: () => boolean) {}

  /** After a sim tick: new shells, a tick of flight for the rest, and the landings. */
  onTick(rateOf: (towerId: number) => number): void {
    const sim = this.sim;
    for (const ev of sim.events.all()) {
      if (ev.kind === SimEventKind.ShellLaunched) {
        const shell = sim.shells.find((x) => x.id === ev.a);
        if (!shell) continue;
        const x = shell.x.toNumber(), z = shell.y.toNumber();
        this.live.set(shell.id, {
          id: shell.id, towerId: ev.b, to: new THREE.Vector3(x, GroundMesh.drawnAt(x, z), z), radius: shell.radius.toNumber(),
          flight: Math.max(1, ev.c), elapsed: 0, missing: 0, release: shellRelease(ev.c, rateOf(ev.b)), from: null, obj: null, rig: null,
          toNext: 0, last: new THREE.Vector3(), spin: Math.random() * 6, marker: null,
        });
      } else if (ev.kind === SimEventKind.ShellLanded) {
        const s = this.live.get(ev.a);
        const landed = sim.landedShells.find((x) => x.id === ev.a);
        const at = s ? s.to : landed ? new THREE.Vector3(landed.x.toNumber(), 0, landed.y.toNumber()) : null;
        if (at && !s) at.y = GroundMesh.drawnAt(at.x, at.z);
        if (at) this.land(at, s?.radius ?? landed?.radius.toNumber() ?? 1);
        if (s) this.drop(s);
      }
    }
    // the flight as the sim has it: the ticks gone are the sim's (a card offer holds the shells while the ticks go on)
    for (const s of this.live.values()) {
      const shell = sim.shells.find((x) => x.id === s.id);
      if (shell) { s.elapsed = s.flight - shell.ticksLeft; s.missing = 0; }
      else if (++s.missing > 2) this.drop(s);                       // gone without a landing we saw: never left hanging
    }
  }

  /** Per frame, at `alpha` of the way into the next tick. */
  update(alpha: number, dt: number): void {
    const vfx = this.fx(), fx = this.reduced() ? null : vfx;
    const held = this.sim.pausedForOffer;                          // the shells hang where they are
    for (const s of this.live.values()) {
      const p = Math.min(1, (s.elapsed + (held ? 0 : alpha)) / s.flight);
      // the landing marker, from the launch: it closes in on the burst's size and brightens as the shell comes down
      // (its clock is the flight's, set every frame, so it waits with the shell through an offer)
      if (vfx) {
        s.marker ??= vfx.decal(s.to, { type: "target", size: s.radius * 2, life: 1, hold: 1, fadeIn: 0.01, color: SHELL_MARKER.color, bright: SHELL_MARKER.bright, rot: Math.random() * 6.3 });
        const m = s.marker;
        m.x = s.to.x; m.z = s.to.z;
        m.size = 2 * s.radius * markerScale(p) / 0.9;
        m.age = Math.min(0.99, p);
        m.alpha = SHELL_MARKER.alpha0 + (SHELL_MARKER.alpha1 - SHELL_MARKER.alpha0) * p * p;
      }
      if (p < s.release) continue;
      if (!s.from) this.release(s);
      const at = shellAt(s, p, _p);
      if (!at || !s.obj) continue;
      const step = _v.subVectors(at, s.last);
      const len = step.length();
      s.obj.position.copy(at);
      if (len > 1e-5) s.obj.quaternion.setFromUnitVectors(_fwd, step.multiplyScalar(1 / len));
      // a finned shell rolls about its own axis as it flies (a tumble end over end read as a dropped bottle)
      s.spin += dt * 9;
      s.obj.rotateZ(s.spin);
      s.rig?.update(dt);
      if (fx && len > 1e-5) {
        // powder smoke off the fuse, one puff per VFX.TRAIL_SPACING.mortar travelled
        let along = s.toNext;
        const dir = _v.subVectors(at, s.last).normalize();
        while (along <= len) { fx.trail("mortar", _t.copy(s.last).addScaledVector(dir, along)); along += SHELL_TRAIL_SPACING; }
        s.toNext = along - len;
      }
      s.last.copy(at);
    }
  }

  /** The sim tick each tower's last muzzle burst played on (one per salvo). */
  private readonly mutedAt = new Map<number, number>();
  /** The sim tick the last full landing burst played on. */
  private burstTick = -1;

  private release(s: MortarShellView): void {
    const from = this.towerNode?.(s.towerId, "bore") ?? null;
    const t = this.sim.findTower(s.towerId);
    s.from = from ?? (t ? new THREE.Vector3(t.cell.x, GroundMesh.drawnAt(t.cell.x, t.cell.y) + 0.9, t.cell.y) : s.to.clone().setY(s.to.y + 2));
    s.last.copy(s.from);
    const L = lib();
    if (L && L.hq("proj_shell")) {
      const spare = this.pool.pop();
      const obj = spare?.obj ?? L.clone("hq:proj_shell");
      const rig = spare?.rig ?? new AnimRig(obj, L.clipsOf("hq:proj_shell"));
      if (!spare) { rig.playLoop("fly", 1, true); this.group.add(obj); }
      obj.visible = true; obj.scale.setScalar(SHELL_SCALE); obj.position.copy(s.from);
      s.obj = obj; s.rig = rig;
    }
    const fx = this.fx();
    // a Rocket Mortar's salvo (tier IV: 3-4 shells at once) gets one muzzle burst, not one per shell — stacked, the
    // smoke clouds and bore flashes whited out the tower (seen in the M21 integration captures)
    const first = this.mutedAt.get(s.towerId) !== this.sim.tick;
    this.mutedAt.set(s.towerId, this.sim.tick);
    if (fx && first) {
      if (this.reduced()) fx.flash(s.from, 0.5, [1, 0.75, 0.4]);
      else fx.muzzle("mortar", s.from, s.to);                      // bore flash, a smoke cloud, a ring of powder
    }
    this.onRelease?.(s, (1 - s.release) * s.flight / SimConstants.TicksPerSecond);
  }

  private land(at: THREE.Vector3, radius: number): void {
    const fx = this.fx();
    if (fx) {
      if (this.reduced()) { fx.flash(_p.copy(at).setY(at.y + 0.3), 1.0, [1, 0.8, 0.5]); fx.ring(at, { size: radius * 2, life: 0.4, color: [1, 0.85, 0.6] }); }
      else {
        // one full burst per tick: a salvo's other shells (tier IV Rocket Mortar) land as their dust ring and crack,
        // so four bursts in one spot do not stack into a white-out
        if (this.burstTick !== this.sim.tick) fx.mortarImpact(at, { trauma: SHELL_TRAUMA });
        else fx.dust(at, 8, { size: 0.6, speed: 2, color: [0.6, 0.5, 0.38] });
        this.burstTick = this.sim.tick;
        // the burst's own reach: a dust ring thrown out to the radius the sim hit, and a crack under the scorch
        fx.ring(at, { size: radius * 2.1, life: 0.5, color: [1, 0.75, 0.45], flat: true, bright: 1.4 });
        fx.decal(at, { type: "crack", size: radius * 1.1, life: 2.6, hold: 1.2, color: [1, 0.5, 0.2], bright: 1 });
      }
    }
    this.onLand?.(at, radius);
  }

  private drop(s: MortarShellView): void {
    this.live.delete(s.id);
    if (s.marker) { s.marker.hold = s.marker.age; s.marker.life = s.marker.age + 0.05; s.marker = null; }
    if (s.obj && s.rig) { s.obj.visible = false; this.pool.push({ obj: s.obj, rig: s.rig }); }
  }

  dispose(): void {
    for (const s of this.live.values()) { if (s.obj) this.group.remove(s.obj); if (s.marker) s.marker.life = s.marker.age; }
    for (const p of this.pool) this.group.remove(p.obj);
    this.live.clear(); this.pool.length = 0;
    this.group.clear();
  }
}
